import { CircularProgress } from '@mui/material';
import { Card, ListGroup, Form, Row, Col } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { successToast, errorToast } from 'utils/toastUtils';
import PersonIcon from '@mui/icons-material/Person';
import Download from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import BeSafeSwitch from 'components/commons/BeSafeSwitch';

import { useState, useEffect, useCallback } from 'react';
import { updateNetwork } from 'app/crud/networks.crud';
import {
  sendEmailToUser,
  regenerateApiKey,
  resetTwoFactorAuth,
  updateTwoFactorAuthEnabled,
  updateAutoMode,
} from 'app/crud/vpn_client_configs.crud';
import {
  Add,
  Delete,
  Edit,
  HelpOutline,
  Remove,
  LaptopMac,
  LaptopWindows,
  PhoneAndroid,
  PhoneIphone,
  SystemUpdateAlt,
  Terminal,
} from '@mui/icons-material';
import BeSafeButton from 'components/commons/BeSafeButton';
import BeSafeTooltip from 'components/commons/BeSafeTooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ConfirmationPopup } from 'components/ConfirmDialog';
import axios from 'axios';
import { getWhereUsed } from 'utils/utils';
import {
  TUNNEL_TYPE_SSL,
  TUNNEL_TYPE_IPSEC,
  TUNNEL_LABEL_SSL,
  TUNNEL_LABEL_IPSEC,
  OS_WINDOWS,
  OS_ANDROID,
  OS_IOS,
  OS_MAC,
  OS_LINUX,
  OS_OTHER,
} from 'utils/constants';

const UserSubList = ({
  previewLoading,
  users,
  selectedUser,
  deleteNetworkObject,
  showEditNetworkModal,
  refetchNetwork,
}) => {
  const whereUsed = getWhereUsed(selectedUser);
  const [ipsecTunnels, setIpsecTunnels] = useState([]);
  const [sslTunnels, setSslTunnels] = useState([]);
  const [disableRegenerateBtn, setDisableRegenerateBtn] = useState(false);
  const [isTwoFactorAuthEnabled, setIsTwoFactorAuthEnabled] = useState(false);

  const [isAutoModeEnabled, setIsAutoModeEnabled] = useState(false);
  const intl = useIntl();
  const downloadUrl = `${axios.defaults.baseURL}/windows_clients/download_windows_exe`;
  const deviceStatuses =
    selectedUser?.vpn_client_config?.vpn_client_devices ||
    [];
  const activeDeviceStatuses = deviceStatuses.filter(
    (device) => device.is_active
  );
  const displayDeviceStatuses = activeDeviceStatuses;


  const allTunnels = [...sslTunnels, ...ipsecTunnels];
  const tunnelTypeById = new Map(
    allTunnels.map((tunnel) => [String(tunnel.id), tunnel.tunnel_type])
  );

  const tunnelTypeLabel = (type) => {
    if (type === TUNNEL_TYPE_SSL) return TUNNEL_LABEL_SSL;
    if (type === TUNNEL_TYPE_IPSEC) return TUNNEL_LABEL_IPSEC;
    return (type || '').toUpperCase();
  };

  const OS_CONFIG = [
    {
      key: OS_WINDOWS,
      name: 'Windows',
      matches: ['windows'],
      replaces: ['windows'],
      icon: <MicrosoftIcon fontSize="small" />,
      deviceIcon: <LaptopWindows fontSize="small" />,
    },
    {
      key: OS_ANDROID,
      name: 'Android',
      matches: ['android'],
      replaces: ['android'],
      icon: <AndroidIcon fontSize="small" />,
      deviceIcon: <PhoneAndroid fontSize="small" />,
    },
    {
      key: OS_IOS,
      name: 'iOS',
      matches: ['ios'],
      replaces: ['ios'],
      icon: <AppleIcon fontSize="small" />,
      deviceIcon: <PhoneIphone fontSize="small" />,
    },
    {
      key: OS_MAC,
      name: 'macOS',
      matches: ['mac', 'osx', 'macos'],
      replaces: ['macos', 'osx', 'mac'],
      icon: <AppleIcon fontSize="small" />,
      deviceIcon: <LaptopMac fontSize="small" />,
    },
    {
      key: OS_LINUX,
      name: 'Linux',
      matches: ['linux'],
      replaces: ['linux'],
      icon: <Terminal fontSize="small" />,
      deviceIcon: <Terminal fontSize="small" />,
    },
  ];

  const parseDeviceOs = (value) => {
    const raw = (value || '').toLowerCase();
    const os = OS_CONFIG.find((config) =>
      config.matches.some((match) => raw.includes(match))
    );

    if (os) {
      const version = os.replaces.reduce(
        (acc, str) => acc.replace(str, ''),
        raw
      ).trim();
      return { key: os.key, name: os.name, version };
    }

    return { key: OS_OTHER, name: value || 'OS:Unknown', version: '' };
  };

  const osIcon = (key) => {
    const os = OS_CONFIG.find((config) => config.key === key);
    if (os) {
      return os.icon;
    }
    return (
      <BeSafeTooltip
        tooltip={<FormattedMessage id="DEVICES.DEVICE_INFO_UNAVAILABLE_TOOLTIP" />}
        placement="bottom"
      >
        <HelpOutline fontSize="small" />
      </BeSafeTooltip>
    );
  };

  const deviceIcon = (key) => {
    const os = OS_CONFIG.find((config) => config.key === key);
    if (os) {
      return os.deviceIcon;
    }
    return (
      <BeSafeTooltip
        tooltip={<FormattedMessage id="DEVICES.DEVICE_INFO_UNAVAILABLE_TOOLTIP" />}
        placement="bottom"
      >
        <HelpOutline fontSize="small" />
      </BeSafeTooltip>
    );
  };

  const versionIcon = (value) => {
    if (!value || value === 'Unknown') {
      return (
        <BeSafeTooltip
          tooltip={<FormattedMessage id="DEVICES.DEVICE_INFO_UNAVAILABLE_TOOLTIP" />}
          placement="bottom"
        >
          <HelpOutline fontSize="small" />
        </BeSafeTooltip>
      );
    }
    return <SystemUpdateAlt fontSize="small" />;
  };

  const sendWindowsInstallerEmail = (besafe_object_id) => {
    if (!besafe_object_id) {
      return errorToast({ body: 'PAGE_RELOAD.PAGE_RELOAD', intl: intl });
    }
    sendEmailToUser(besafe_object_id)
      .then(() => {
        successToast({
          body: 'ADMIN_USERS.WINDOWS_EXE_EMAIL_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        errorToast({ body: err.response.data?.error, intl: intl });
      });
  };

  const handleApiKeyRegenerate = (user_id) => {
    if (!user_id) return;

    setDisableRegenerateBtn(true);
    ConfirmationPopup({
      title: intl.formatMessage({
        id: 'GENERAL.WARNING',
      }),
      description: intl.formatMessage({
        id: 'USER_OBJECTS.CONFIRM_API_REGENERATE',
      }),
      okLabel: intl.formatMessage({
        id: 'GENERAL.OK',
      }),
      cancelLabel: intl.formatMessage({
        id: 'GENERAL.CANCEL',
      }),
      okAction: () => {
        regenerateApiKey(user_id)
          .then(() => {
            refetchNetwork();
            successToast({
              body: 'USER_OBJECTS.API_KEY_REGENERATE_SUCCESS',
              intl: intl,
            });
          })
          .catch((err) => {
            errorToast({ body: err.response?.data?.error, intl: intl });
          });
      },
    });
    setDisableRegenerateBtn(false);
  };

  const handleResetTwoFactorAuth = (user_id) => {
    if (!user_id) return;

    ConfirmationPopup({
      title: intl.formatMessage({
        id: 'GENERAL.WARNING',
      }),
      description: intl.formatMessage({
        id: 'USER_OBJECTS.CONFIRM_RESET_TWO_FACTOR_AUTH',
      }),
      okLabel: intl.formatMessage({
        id: 'GENERAL.OK',
      }),
      cancelLabel: intl.formatMessage({
        id: 'GENERAL.CANCEL',
      }),
      okAction: () => {
        resetTwoFactorAuth(user_id)
          .then(() => {
            refetchNetwork();
            successToast({
              body: 'USER_OBJECTS.RESET_TWO_FACTOR_AUTH_SUCCESS',
              intl: intl,
            });
          })
          .catch((err) => {
            errorToast({ body: err.response?.data?.error, intl: intl });
          });
      },
    });
  };

  const handleUpdateTwoFactorAuthEnabled = (val) => {
    setIsTwoFactorAuthEnabled(val);
    updateTwoFactorAuthEnabled(selectedUser.id, val)
      .then(() => {
        successToast({
          body: 'USER_OBJECTS.EDIT_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        setIsTwoFactorAuthEnabled(!val);
        errorToast({ body: err.response.data?.error, intl: intl });
      });
  };

  const handleAutoModeToggle = (val) => {
    setIsAutoModeEnabled(val);
    updateAutoMode(selectedUser.id, val)
      .then(() => {
        successToast({
          body: 'USER_OBJECTS.AUTO_MODE_UPDATE_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        setIsAutoModeEnabled(!val);
        errorToast({ body: err.response?.data?.error, intl: intl });
      });
  };

  const userIsActive = (user) => {
    const userIsNonEditable = !user.editable;
    const userDataExists = 'data' in user;
    const activeKeyExists = 'active' in user.data;
    return (
      userIsNonEditable && userDataExists && activeKeyExists && user.data.active
    );
  };

  const detachIPV4Address = (index) => {
    let newIpv4s = [...selectedUser.data?.ipv4_addresses];
    newIpv4s.splice(index, 1);

    let params = {
      name: selectedUser.name,
      email: selectedUser.data?.email,
      group: selectedUser.data?.group,
      domain: selectedUser.data?.domain,
      computer: selectedUser.data?.computer,
      ipv4_addresses: newIpv4s,
      ipv6_addresses: selectedUser.data?.ipv6_addresses,
      mac_addresses: selectedUser.data?.mac_addresses,
      source: selectedUser.data?.source,
      type: 'user',
    };

    updateUser(params);
  };

  const detachIPV6Address = (index) => {
    let newIpv6s = [...selectedUser.data?.ipv6_addresses];
    newIpv6s.splice(index, 1);

    let params = {
      name: selectedUser.name,
      email: selectedUser.data?.email,
      group: selectedUser.data?.group,
      domain: selectedUser.data?.domain,
      computer: selectedUser.data?.computer,
      ipv4_addresses: selectedUser.data?.ipv4_addresses,
      ipv6_addresses: newIpv6s,
      mac_addresses: selectedUser.data?.mac_addresses,
      source: selectedUser.data?.source,
      type: 'user',
    };

    updateUser(params);
  };

  const detachMacAddress = (index) => {
    let newMacs = [...selectedUser.data?.mac_addresses];
    newMacs.splice(index, 1);

    let params = {
      name: selectedUser.name,
      email: selectedUser.data?.email,
      group: selectedUser.data?.group,
      domain: selectedUser.data?.domain,
      computer: selectedUser.data?.computer,
      ipv4_addresses: selectedUser.data?.ipv4_addresses,
      ipv6_addresses: selectedUser.data?.ipv6_addresses,
      mac_addresses: newMacs,
      source: selectedUser.data?.source,
      type: 'user',
    };

    updateUser(params);
  };

  const updateUser = (params) => {
    updateNetwork({ ...params, id: selectedUser.id })
      .then(() => {
        refetchNetwork();

        successToast({
          body: 'USER_OBJECTS.EDIT_SUCCESS',
          intl: intl,
        });
      })
      .catch((err) => {
        errorToast({ body: err.response.data?.error, intl: intl });
      });
  };

  const extractRemoteTunnelsByType = useCallback(
    (type) => {
      return selectedUser?.vpn_tunnels
        ?.concat(selectedUser?.besafe_groups?.flatMap((obj) => obj.vpn_tunnels))
        ?.filter(
          (tunnel, index, self) =>
            index === self.findIndex((t) => t.id === tunnel.id)
        )
        ?.filter((tunnel) => tunnel.tunnel_type === type);
    },
    [selectedUser]
  );

  useEffect(() => {
    if (selectedUser.id) {
      setSslTunnels(extractRemoteTunnelsByType(TUNNEL_TYPE_SSL));
      setIpsecTunnels(extractRemoteTunnelsByType(TUNNEL_TYPE_IPSEC));
      setIsTwoFactorAuthEnabled(
        selectedUser?.vpn_client_config?.is_two_factor_auth_enabled ?? true
      );
      setIsAutoModeEnabled(
        selectedUser?.vpn_client_config?.is_auto_mode_enabled || false
      );
    }
  }, [selectedUser.id, selectedUser, extractRemoteTunnelsByType]);

  const handleDownloadClick = async () => {
    try {
      const response = await fetch(downloadUrl);

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Download request failed');
      }

      window.open(json.download_url, '_blank');

      successToast({
        body: 'AGENT_MAILER.DOWNLOAD_SUCCESS',
        intl,
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error.message;

      errorToast({
        body: errorMessage,
        intl,
      });
    }
  };

  return (
    <>
      {previewLoading ? (
        <div className="d-flex card justify-content-center align-items-center w-60 h-100 mr3">
          <CircularProgress />
        </div>
      ) : users.length <= 0 ? (
        <span className="card w-60 h-100 mr-3 p-3 text-center pt-5">
          <FormattedMessage id="GENERAL.NO_DATA" />
        </span>
      ) : (
        <Card className="w-60 h-100 mr-3">
          <Card.Header>
            <Card.Title className="d-flex justify-content-between py-0 my-0 text-cus-heading">
              <span className="my-auto">
                <PersonIcon className="mr-2" />
                {selectedUser.name}
              </span>
              {(selectedUser.editable || !userIsActive(selectedUser)) && (
                <div>
                  {selectedUser.editable && (
                    <BeSafeButton
                      variant="transparent"
                      className="p-0 px-1"
                      onClick={showEditNetworkModal}
                      icon={<Edit className="icon-table-md" />}
                      tooltip="GENERAL.EDIT_SMALL"
                    />
                  )}
                  <BeSafeButton
                    variant="transparent"
                    className="p-0 px-1"
                    onClick={deleteNetworkObject}
                    icon={<Delete className="icon-table-md" />}
                    tooltip="GENERAL.DELETE_SMALL"
                  />
                </div>
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body className="overflow-auto">
            <div className="d-flex py-2">
              <span className="font-weight-bold">
                <FormattedMessage id="NETWORK_OBJECTS.DOMAIN" />
              </span>
              <span>{`: ${selectedUser?.data?.domain || ''}`}</span>
            </div>

            <div className="d-flex py-2">
              <span className="font-weight-bold">
                <FormattedMessage id="NETWORK_OBJECTS.GROUP" />
              </span>
              <span>{`: ${selectedUser?.data?.group || ''}`}</span>
            </div>

            <div className="d-flex py-2">
              <span className="font-weight-bold">
                <FormattedMessage id="NETWORK_OBJECTS.HOSTNAME" />
              </span>
              <span>{`: ${selectedUser?.data?.computer || ''}`}</span>
            </div>

            <div className="d-flex justify-content-between font-weight-bold py-2">
              <FormattedMessage id="OBJECT_EXPLORER.IPV4_LIST" />
              {selectedUser?.editable && (
                <BeSafeButton
                  variant="transparent"
                  className="p-0 mr-3"
                  onClick={showEditNetworkModal}
                  icon={<Add />}
                  tooltip="OBJECT_MODAL.ADD"
                />
              )}
            </div>
            <ListGroup>
              {selectedUser?.data?.ipv4_addresses?.map((address, index) => (
                <ListGroup.Item
                  key={address}
                  className="d-flex justify-content-between py-0"
                >
                  <span className="w-75 py-2">{address}</span>
                  {selectedUser?.editable && (
                    <div className="my-auto">
                      <BeSafeButton
                        variant="transparent"
                        className="p-0"
                        disabled={false}
                        onClick={() => detachIPV4Address(index)}
                        icon={<Remove />}
                        tooltip="GENERAL.DELETE_SMALL"
                      />
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>

            <div className="d-flex justify-content-between font-weight-bold py-2 mt-3">
              <FormattedMessage id="OBJECT_EXPLORER.IPV6_LIST" />
              {selectedUser?.editable && (
                <BeSafeButton
                  variant="transparent"
                  className="p-0 mr-3"
                  onClick={showEditNetworkModal}
                  icon={<Add />}
                  tooltip="OBJECT_MODAL.ADD"
                />
              )}
            </div>
            <ListGroup>
              {selectedUser?.data?.ipv6_addresses?.map((address, index) => (
                <ListGroup.Item
                  key={address}
                  className="d-flex justify-content-between py-0"
                >
                  <span className="w-75 py-2">{address}</span>
                  {selectedUser?.editable && (
                    <div className="my-auto">
                      <BeSafeButton
                        variant="transparent"
                        className="p-0"
                        disabled={false}
                        onClick={() => detachIPV6Address(index)}
                        icon={<Remove />}
                        tooltip="GENERAL.DELETE_SMALL"
                      />
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>

            <div className="d-flex justify-content-between font-weight-bold py-2 mt-3">
              <FormattedMessage id="OBJECT_EXPLORER.MAC_ADDRESSES" />
              {selectedUser?.editable && (
                <BeSafeButton
                  variant="transparent"
                  className="p-0 mr-3"
                  onClick={showEditNetworkModal}
                  icon={<Add />}
                  tooltip="OBJECT_MODAL.ADD"
                />
              )}
            </div>
            <ListGroup>
              {selectedUser?.data?.mac_addresses?.map((address, index) => (
                <ListGroup.Item
                  key={address}
                  className="d-flex justify-content-between py-0"
                >
                  <span className="w-75 py-2">{address}</span>
                  {selectedUser?.editable && (
                    <div className="my-auto">
                      <BeSafeButton
                        variant="transparent"
                        className="p-0"
                        disabled={false}
                        onClick={() => detachMacAddress(index)}
                        icon={<Remove />}
                        tooltip="GENERAL.DELETE_SMALL"
                      />
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>

            {selectedUser?.data?.source && (
              <div className="d-flex py-2">
                <span className="font-weight-bold">
                  <FormattedMessage id="NETWORK_OBJECTS.SOURCE" />
                </span>
                <span>{`: ${selectedUser?.data?.source.join(',') || ''}`}</span>
              </div>
            )}

            <div className="d-flex py-2">
              <span className="font-weight-bold">Status</span>
              <span>{`: ${selectedUser?.data?.active ? 'On' : 'Off'}`}</span>
            </div>

            <div className="d-flex py-2 mt-3 mb-3 border-top border-bottom">
              <span className="font-weight-bold text-cus-heading">
                <FormattedMessage id="USER_OBJECTS.BESAFE_VPN_AGENT" />
              </span>
            </div>

            <>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="OBJECT_EXPLORER.USER_BUILD" />:
                  </Form.Label>
                </Col>
                <Col md={8}>
                  <BeSafeButton
                    className="px-1 py-0 mr-1"
                    variant="primary"
                    onClick={handleDownloadClick}
                    icon={<Download className="icon-table" />}
                    disabled={!selectedUser?.vpn_client_config}
                    tooltip="USER_OBJECTS.DOWNLOAD_BUILD"
                  />
                  <BeSafeButton
                    className="px-1 py-0 mr-1 ml-1"
                    variant="primary"
                    onClick={() => sendWindowsInstallerEmail(selectedUser?.id)}
                    icon={<EmailIcon className="icon-table" />}
                    disabled={!selectedUser?.vpn_client_config}
                    tooltip="USER_OBJECTS.SEND_EMAIL"
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.API_KEY" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <span>{selectedUser?.vpn_client_config?.api_key || ''}</span>
                  <BeSafeButton
                    className="px-1 py-0 mr-1 ml-2"
                    variant="primary"
                    onClick={() => handleApiKeyRegenerate(selectedUser?.id)}
                    icon={<RefreshIcon className="icon-table" />}
                    disabled={disableRegenerateBtn}
                    tooltip="USER_OBJECTS.REGENERATE_API_KEY"
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="NETWORK_OBJECTS.OBSERVATION_ID" />
                  </Form.Label>
                </Col>
                <Col md={8}>
                  <span>{selectedUser?.data?.observation_id || '-'}</span>
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.AUTO_CONNECT_MODE" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <BeSafeSwitch
                    name="auto_mode"
                    state={isAutoModeEnabled}
                    onChange={(val) => handleAutoModeToggle(val)}
                    disabled={!selectedUser?.vpn_client_config}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.TWO_FACTOR_AUTH_ENABLED" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <BeSafeSwitch
                    name="two_factor_auth"
                    state={isTwoFactorAuthEnabled}
                    onChange={(val) => handleUpdateTwoFactorAuthEnabled(val)}
                    disabled={!selectedUser?.editable}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <Form.Label className="mb-0 font-weight-bold">
                    <FormattedMessage id="USER_OBJECTS.TWO_FACTOR_AUTH_SETUP_COMPLETED" />
                  </Form.Label>
                </Col>
                <Col md={8} className="d-flex align-items-center">
                  <FormattedMessage
                    id={
                      selectedUser?.vpn_client_config?.is_two_factor_auth_setup_completed
                        ? 'GENERAL.YES'
                        : 'GENERAL.NO'
                    }
                  />
                  <BeSafeButton
                    className="px-1 py-0 mr-1 ml-2"
                    variant="primary"
                    onClick={() => handleResetTwoFactorAuth(selectedUser?.id)}
                    icon={<RefreshIcon className="icon-table" />}
                    disabled={!selectedUser?.vpn_client_config?.is_two_factor_auth_setup_completed}
                    tooltip="USER_OBJECTS.RESET_TWO_FACTOR_AUTH"
                  />
                </Col>
              </Form.Group>
              {sslTunnels.length > 0 && (
                <Form.Group className="mt-3 mb-2">
                  <Col md={4} className="pl-0">
                    <span className="mb-2 font-weight-bold">
                      <FormattedMessage id="OBJECT_EXPLORER.SSL_PROFILE" />
                    </span>
                  </Col>
                  <Col md={8} className="pl-0">
                    <div className="d-flex flex-column pl-2 mt-2">
                      {sslTunnels.map((tunnel) => {
                        const isConnected = activeDeviceStatuses.some(
                          (device) => String(device.active_tunnel_profile_id) === String(tunnel.id)
                        );
                        const tunnelStatus = isConnected ? 'up' : (tunnel.status || tunnel.tunnel_status || 'down');
                        const isUp = tunnelStatus === 'up' || tunnelStatus === 'full_up';
                        return (
                          <div key={tunnel.id} className="d-flex align-items-center justify-content-between w-100 mb-2">
                            <span className="flex-grow-1">{tunnel.name || tunnel.id}</span>
                            <span className={`besafe-tunnel-badge ${isUp ? 'active' : ''}`}>
                              <FormattedMessage
                                id={
                                  tunnelStatus === 'up'
                                    ? 'DEVICES.TUNNEL_UP'
                                    : tunnelStatus === 'full_up'
                                    ? 'DEVICES.TUNNEL_FULL_UP'
                                    : 'DEVICES.TUNNEL_DOWN'
                                }
                              />
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </Form.Group>
              )}
              {ipsecTunnels.length > 0 && (
                <Form.Group as={Row} className="mt-3 mb-2">
                  <Col md={4} className="pl-0">
                    <span className="mb-2 font-weight-bold">
                      <FormattedMessage id="OBJECT_EXPLORER.IPSEC_PROFILE" />
                    </span>
                  </Col>
                  <Col md={8} className="pl-0">
                    <div className="d-flex flex-column pl-0">
                      {ipsecTunnels.map((tunnel) => {
                        const isConnected = activeDeviceStatuses.some(
                          (device) => String(device.active_tunnel_profile_id) === String(tunnel.id)
                        );
                        const tunnelStatus = isConnected ? 'up' : (tunnel.status || tunnel.tunnel_status || 'down');
                        const isUp = tunnelStatus === 'up' || tunnelStatus === 'full_up';
                        return (
                          <div key={tunnel.id} className="d-flex align-items-center justify-content-between w-100 mb-2">
                            <span className="font-weight-bold mr-3">
                              <FormattedMessage id="OBJECT_EXPLORER.IPSEC_PROFILE" />:
                            </span>
                            <span className="flex-grow-1">{tunnel.name || tunnel.id}</span>
                            <span className={`besafe-tunnel-badge ${isUp ? 'active' : ''}`}>
                              <FormattedMessage
                                id={
                                  tunnelStatus === 'up'
                                    ? 'DEVICES.TUNNEL_UP'
                                    : tunnelStatus === 'full_up'
                                    ? 'DEVICES.TUNNEL_FULL_UP'
                                    : 'DEVICES.TUNNEL_DOWN'
                                }
                              />
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </Form.Group>
              )}
              <Form.Group as={Row} className="align-items-center mb-3">
                <Col md={4}>
                  <span className="mb-0 font-weight-bold">
                    <FormattedMessage id="OBJECT_EXPLORER.AGENT_STATUS" />
                  </span>
                </Col>
                <Col md={8} className="d-flex justify-content-start align-items-center">
                  {activeDeviceStatuses.length === 0 && (
                    <span className="text-muted">
                      <FormattedMessage id="OBJECT_EXPLORER.INACTIVE" />
                    </span>
                  )}
                </Col>
              </Form.Group>
              {displayDeviceStatuses.length > 0 && (
                <Row className="mx-n2">
                  {displayDeviceStatuses.map((device) => {
                    const osMeta = parseDeviceOs(device.device_os);
                    const osDisplay = device.device_os
                      ? `${osMeta.name}${osMeta.version ? ` ${osMeta.version}` : ''}`
                      : intl.formatMessage({ id: 'OBJECT_EXPLORER.OS_UNKNOWN' });
                    const deviceName = device.device_name || intl.formatMessage({ id: 'OBJECT_EXPLORER.DEVICE_NAME_UNKNOWN' });
                    const appVersion = device.application_version || intl.formatMessage({ id: 'UNKNOWN' });
                    const isActive = !!device.is_active;
                    const tunnelName = device.active_tunnel_name;
                    const tunnelId = String(device.active_tunnel_profile_id || '');
                    const tunnelType = tunnelTypeLabel(
                      tunnelTypeById.get(tunnelId)
                    );
                    const connectedLabel = tunnelName
                      ? intl.formatMessage(
                          { id: 'OBJECT_EXPLORER.CONNECTED_TUNNEL' },
                          { type: tunnelType, name: tunnelName }
                        )
                      : '';
                    const statusLabel = isActive
                      ? intl.formatMessage({ id: 'OBJECT_EXPLORER.RUNNING' })
                      : intl.formatMessage({ id: 'OBJECT_EXPLORER.INACTIVE' });
                    const statusColor = isActive ? '#2E7D32' : '#9E9E9E';
                    const statusDotColor = isActive ? '#2E7D32' : '#BDBDBD';
                    return (
                      <Col xs={12} sm={6} md={6} key={device.device_id || device.device_name} className="mb-3 px-2">
                        <Card className="h-100 shadow-sm" style={{borderRadius: '10px', border: '0.5px solid #949494' }} >
                          <Card.Body style={{ padding: '14px' }}>
                            <div className="d-flex flex-column" style={{ minHeight: 96 }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="font-weight-bold" style={{ fontSize: '16px' }}>
                                  {deviceName}
                                </div>
                                <div>{deviceIcon(osMeta.key)}</div>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div style={{ fontSize: '14px' }}>
                                  {osDisplay}
                                </div>
                                <div>{osIcon(osMeta.key)}</div>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div style={{ fontSize: '14px' }}>
                                  {`Version: ${appVersion}`}
                                </div>
                                <div>{versionIcon(device.application_version)}</div>
                              </div>
                              <div
                                className="d-flex justify-content-between align-items-center"
                                style={{
                                  visibility: connectedLabel ? 'visible' : 'hidden',
                                }}
                              >
                                <div style={{ fontSize: '14px' }}>
                                  {connectedLabel || '\u00A0'}
                                </div>
                                <div>
                                  <DeviceHubIcon fontSize="small" />
                                </div>
                              </div>
                            </div>
                            <div
                              className={`d-flex align-items-center rounded px-3 py-2 mt-2 ${
                                connectedLabel ? 'justify-content-between' : 'justify-content-center'
                              }`} style={{ backgroundColor: '#F8F9F8', border: '0.5px solid #949494' }}
                            >
                              {connectedLabel && (
                              <div className="d-flex align-items-center" style={{ color: statusDotColor, fontSize: '14px' }}>
                                <span>Connected</span>
                                <span
                                  className="ml-2"
                                    style={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: statusColor,
                                      display: 'inline-block',
                                  }}
                                />
                              </div>
                              )}
                              <div className="d-flex align-items-center" style={{ color: statusColor, fontSize: '14px' }}>
                                <span>{statusLabel}</span>
                                <span
                                  className="ml-2"
                                  style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: statusDotColor,
                                    display: 'inline-block',
                                  }}
                                />
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </>
            {selectedUser?.besafe_groups?.length > 0 && (
              <>
                <div className="d-flex py-2">
                  <span className="font-weight-bold">
                    <FormattedMessage id="NETWORK_OBJECTS.GROUPS" />
                  </span>
                </div>
                <ListGroup>
                  {selectedUser?.besafe_groups?.map((g, index) => (
                    <ListGroup.Item
                      key={g.id}
                      className="d-flex justify-content-between py-0"
                    >
                      <span className="w-75 py-2">{g.name}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            )}
          </Card.Body>
          <Card.Footer className="obj-where-used">
            <p className="font-weight-bold my-0">
              <FormattedMessage id="GENERAL.WHERE_USED" />:
            </p>
            <span>
              {whereUsed?.length <= 0 ? (
                <FormattedMessage id="GENERAL.NOT_IN_USE" />
              ) : (
                whereUsed?.map((r) => r).join(', ')
              )}
            </span>
            {selectedUser?.data?.azure_ad_user_info?.tenant_name && (
              <div className="mt-2">
                <p className="font-weight-bold my-0">
                  <FormattedMessage id="OBJECT_EXPLORER.AZURE_TENANT_NAME" />:
                </p>
                <span className="font-italic">
                  {selectedUser?.data?.azure_ad_user_info?.tenant_name}
                </span>
              </div>
            )}
          </Card.Footer>
        </Card>
      )}
    </>
  );
};

export default UserSubList;
